import {MutableRefObject, useEffect} from 'react';
import {getJsonItem, setItem} from "../../../services/device/localStorage";

/**
 * This file defines a custom React hook named `useScroll`. This hook is used to manage the scroll position of a
 * scrollable content element, saving and restoring the scroll position using local storage. It also handles window
 * resize and device orientation change events to ensure the scroll position is correctly maintained across different
 * screen sizes and orientations. The hook provides the following functionalities:
 *
 * 1. **Restores Scroll Position**: On component mount, it retrieves the last saved scroll position from local storage
 *    and sets the scroll position of the scrollable content element.
 * 2. **Saves Scroll Position**: It saves the current scroll position to local storage whenever the window is resized,
 *    the device orientation changes, or the user scrolls the content.
 * 3. **Event Listeners**: It adds event listeners for `resize`, `deviceorientation`, and `scroll` events to handle the
 *    saving of scroll data appropriately.
 * 4. **Cleanup**: It removes the event listeners when the component is unmounted to prevent memory leaks.
 */

export default function useScroll(scrollableContentRef: MutableRefObject<HTMLElement>, scrollDataStorageKey: string, deps: any[] = []){

  useEffect(() => {
    //get last scroll position from localstorage and set the scroll position on the scrollable content.
    const scrollData = getJsonItem<IScrollData>(scrollDataStorageKey);
    scrollContentOrResetLocalStorage(scrollData, scrollableContentRef, scrollDataStorageKey);
    //save scroll data to local storage anytime the window size is changed.
    function onWindowResize(){
      saveCurrentScrollData(scrollableContentRef, scrollDataStorageKey);
    }

    function onDeviceOrientationChanged() {
      const scrollData = getJsonItem<IScrollData>(scrollDataStorageKey);

      if (window.innerWidth !== scrollData?.windowWidth) {
        saveCurrentScrollData(scrollableContentRef, scrollDataStorageKey);
      }
    }

    window.addEventListener('resize', onWindowResize);
    window.addEventListener('deviceorientation', onDeviceOrientationChanged);
    scrollableContentRef.current.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('resize', onWindowResize);
      window.removeEventListener('deviceorientation', onDeviceOrientationChanged);
      scrollableContentRef?.current?.removeEventListener('scroll', onScroll);
    };
  }, [scrollDataStorageKey, ...deps]); //scroll data key can change for a component.  e.g. course lessons starts out with an empty view model initially.

  let scrollTimeoutId: number | undefined;
  function onScroll() {
    if (scrollTimeoutId) {
      clearTimeout(scrollTimeoutId);
    }

    scrollTimeoutId = window.setTimeout(() => {
      saveCurrentScrollData(scrollableContentRef, scrollDataStorageKey);
      scrollTimeoutId = undefined;
    }, 100);
  }
}

function scrollContentOrResetLocalStorage(scrollData: IScrollData | undefined, scrollableContentRef: MutableRefObject<HTMLElement>, scrollDataStorageKey: string) {
  if(scrollData) {
    if (scrollData.windowWidth === window.innerWidth) {
      scrollableContentRef.current.scrollTop = scrollData.scrollTop;
    } else {
      localStorage.removeItem(scrollDataStorageKey);
    }
  }
}

interface IScrollData {
  scrollTop: number,
  windowWidth: number,
}

function saveCurrentScrollData(scrollableContentRef: MutableRefObject<HTMLElement>, scrollDataStorageKey: string) {
  if(!scrollableContentRef || !scrollableContentRef.current) {
    return;
  }
  const scrollTop = scrollableContentRef.current.scrollTop;
  const windowWidth = window.innerWidth;
  setItem<IScrollData>(scrollDataStorageKey, {scrollTop, windowWidth});
}
